/**
 * Calendar view (month/week/day) for sessions, field events, post-its, and holidays.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, View, type ToolbarProps } from 'react-big-calendar';
import {
  CALENDAR_CLUSTER_MAX_VISIBLE,
  CALENDAR_DAY_VIEW_MAX,
  CALENDAR_DAY_VIEW_MIN,
  CALENDAR_DAY_VIEW_SCROLL_TO,
  CALENDAR_LEGEND_ITEMS,
  CALENDAR_PERIOD_LABEL,
  CALENDAR_RBC_MESSAGES,
  EVENT_COLOR_MAP,
  DEFAULT_CALENDAR_EVENT_TYPES,
  type CalendarLegendItem,
} from '@/components/calendar/calendar-configs';
import {
  additionalEventsAriaLabel,
  alignAgendaColumnTitles,
  applyCalendarViewPreference,
  applyVacationZonePreference,
  buildRbcEventsForView,
  calendarEventTypesForMode,
  capitalizeFrenchMonthLabel,
  calendarLocalizer,
  createDayEventsBadge,
  filterCalendarEventsByType,
  filterCalendarEventsByVacationZone,
  getCalendarEventBlockStyle,
  getCalendarEventDisplayTitle,
  getCalendarFetchRange,
  getEventsOnDay,
  getFullCalendarPrimaryEvent,
  getMonthOverflowEvents,
  getTimeGridHiddenEventsOnDay,
  isCalendarAgendaLaneRbcEvent,
  parseCalendarView,
  resolveCellCalendarDate,
  splitAgendaLaneEvents,
  timeGridHeaderDates,
  toCalendarPreferenceRecord,
  toggleCalendarEventType,
  useEventTypeIcons,
} from '@/components/calendar/calendar-utils';
import { usePreferencesConfig } from '@/hooks/usePreferencesConfig';
import { getCalendarEvents } from '@/services/calendar.service';
import { useMode } from '@/contexts/ModeContext';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  CalendarEvent,
  CalendarEventType,
  type CalendarPrefs,
  type CalendarRbcEvent,
} from '@/types/calendar.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import { DEFAULT_HOLIDAY_FILTERS } from '@/components/holidays/holiday-configs';
import type { CalendarSyncFilterSeed } from '@/utils/calendar-sync-utils';
import { CalendarFilters } from './CalendarFilters';
import { CalendarEventModal } from './CalendarEventModal';
import { cn } from '@/lib/utils';
import 'react-big-calendar/lib/css/react-big-calendar.css';

/** View names RBC passes either as a list or as a map of enabled views. */
function toolbarViewNames(views: ToolbarProps['views']): View[] {
  if (Array.isArray(views)) return views;
  return Object.keys(views) as View[];
}

/** Prev / current-period / next, then the view switch. */
function CalendarToolbar({ label, view, views, onNavigate, onView }: Readonly<ToolbarProps>) {
  const viewNames = toolbarViewNames(views);

  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group calendar-toolbar-nav">
        <button type="button" onClick={() => onNavigate('PREV')}>
          {CALENDAR_RBC_MESSAGES.previous}
        </button>
        <button type="button" onClick={() => onNavigate('TODAY')}>
          {CALENDAR_PERIOD_LABEL[view]}
        </button>
        <button type="button" onClick={() => onNavigate('NEXT')}>
          {CALENDAR_RBC_MESSAGES.next}
        </button>
      </span>
      <span className="rbc-toolbar-label">{capitalizeFrenchMonthLabel(label)}</span>
      {viewNames.length > 1 && (
        <span className="rbc-btn-group calendar-toolbar-views">
          {viewNames.map((name) => (
            <button
              type="button"
              key={name}
              className={cn(view === name && 'rbc-active')}
              onClick={() => onView(name)}
            >
              {CALENDAR_RBC_MESSAGES[name]}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}

/** Props for the agenda event cell (single chip or hour-lane chips + `+K`). */
interface AgendaEventProps {
  event: CalendarRbcEvent;
  title: string;
  onSelectLaneEvent: (laneEvent: CalendarEvent) => void;
  onSelectLaneOverflow: (laneDate: Date, laneEvents: CalendarEvent[]) => void;
}

/** One colored agenda chip (icon + label). */
function AgendaEventChip({
  laneEvent,
  label,
  onSelect,
}: Readonly<{
  laneEvent: CalendarEvent;
  label: string;
  onSelect: (laneEvent: CalendarEvent) => void;
}>) {
  const typeIcons = useEventTypeIcons();
  const colors = EVENT_COLOR_MAP[laneEvent.type] ?? EVENT_COLOR_MAP.session;
  const Icon = typeIcons[laneEvent.type] ?? typeIcons.session;

  function handleClick(clickEvent: React.MouseEvent<HTMLButtonElement>): void {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    onSelect(laneEvent);
  }

  return (
    <button
      type="button"
      className="calendar-agenda-event-frame"
      style={{ background: colors.bg, borderColor: colors.border }}
      onClick={handleClick}
    >
      <Icon className="calendar-agenda-event-icon" aria-hidden />
      {label}
    </button>
  );
}

/** Agenda cell: hour-lane chips + `+K`, or a single typed chip. */
function AgendaEvent({
  event,
  title,
  onSelectLaneEvent,
  onSelectLaneOverflow,
}: Readonly<AgendaEventProps>) {
  const typeIcons = useEventTypeIcons();
  const laneEvents = event.laneEvents;

  if (laneEvents && laneEvents.length > 0) {
    const { visible, hiddenCount } = splitAgendaLaneEvents(
      laneEvents,
      CALENDAR_CLUSTER_MAX_VISIBLE.agenda,
    );
    const hiddenEvents = laneEvents.slice(visible.length);

    function handleOverflowClick(clickEvent: React.MouseEvent<HTMLButtonElement>): void {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      onSelectLaneOverflow(event.start, hiddenEvents);
    }

    function renderLaneChip(laneEvent: CalendarEvent): React.ReactNode {
      return (
        <AgendaEventChip
          key={laneEvent.id}
          laneEvent={laneEvent}
          label={getCalendarEventDisplayTitle(laneEvent)}
          onSelect={onSelectLaneEvent}
        />
      );
    }

    return (
      <span className="calendar-agenda-lane">
        {visible.map(renderLaneChip)}
        {hiddenCount > 0 ? (
          <button
            type="button"
            className="calendar-agenda-lane-more"
            onClick={handleOverflowClick}
            aria-label={additionalEventsAriaLabel(hiddenCount)}
          >
            +{hiddenCount}
          </button>
        ) : null}
      </span>
    );
  }

  const colors = EVENT_COLOR_MAP[event.resource.type] ?? EVENT_COLOR_MAP.session;
  const Icon = typeIcons[event.resource.type] ?? typeIcons.session;
  return (
    <span
      className="calendar-agenda-event-frame"
      style={{ background: colors.bg, borderColor: colors.border }}
    >
      <Icon className="calendar-agenda-event-icon" aria-hidden />
      {title}
    </span>
  );
}

/** Props for {@link CalendarView}. */
interface CalendarViewProps {
  /** Optional: parent opens an edit surface from the event modal. */
  onSelectEvent?: (event: CalendarEvent) => void;
  /** Dashboard section width; labels show on `full` and `2/3`. */
  layoutWidth?: ElementWidth;
  /** Push type chips + vacation zone to the calendar-sync modal. */
  onSyncSeedChange?: (seed: CalendarSyncFilterSeed) => void;
}

/** Month/week/day/agenda calendar: fetch, filters, month `+N` badges, event modal. */
export const CalendarView: React.FC<CalendarViewProps> = ({
  onSelectEvent,
  layoutWidth = 'full',
  onSyncSeedChange,
}) => {
  const { mode } = useMode();
  const showFieldEvents = mode !== 'remote';
  const legendItems = CALENDAR_LEGEND_ITEMS.filter(
    (item) => showFieldEvents || item.type !== 'event',
  );
  const {
    config: calendarConfig,
    saveConfig: saveCalendarConfig,
    isLoaded,
  } = usePreferencesConfig('calendar');
  const cal = (calendarConfig || {}) as CalendarPrefs;
  const vacationZoneFilter = cal.vacationZoneId;
  const preferredView = parseCalendarView(cal.defaultView);

  const [enabledTypes, setEnabledTypes] = React.useState(() => [...DEFAULT_CALENDAR_EVENT_TYPES]);
  const [view, setView] = React.useState<View>(() => preferredView);
  const [date, setDate] = React.useState(() => new Date());
  const [dayEventsModalDate, setDayEventsModalDate] = React.useState<Date | null>(null);
  const [dayEventsModalEvents, setDayEventsModalEvents] = React.useState<CalendarEvent[]>([]);
  const [selectedEventInModal, setSelectedEventInModal] = React.useState<CalendarEvent | null>(
    null,
  );

  React.useEffect(() => {
    setView(preferredView);
  }, [preferredView]);

  const dateRange = React.useMemo(() => getCalendarFetchRange(date, view), [date, view]);
  const requestedTypes = React.useMemo(
    () => calendarEventTypesForMode(enabledTypes, mode),
    [enabledTypes, mode],
  );

  React.useEffect(() => {
    onSyncSeedChange?.({
      holidayFilters: {
        ...DEFAULT_HOLIDAY_FILTERS,
        vacationZoneId: vacationZoneFilter,
      },
    });
  }, [onSyncSeedChange, vacationZoneFilter]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'calendar-events',
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
      requestedTypes,
      vacationZoneFilter ?? '',
      mode ?? '',
    ],
    queryFn: () =>
      getCalendarEvents(
        dateRange.start,
        dateRange.end,
        requestedTypes,
        vacationZoneFilter,
        mode ?? undefined,
      ),
    enabled: isLoaded,
  });

  const fetchedEvents = React.useMemo(() => {
    const filtered = filterCalendarEventsByVacationZone(data?.events ?? [], vacationZoneFilter);
    if (mode !== 'remote') return filtered;
    return filtered.filter((event) => event.type !== 'event');
  }, [data?.events, vacationZoneFilter, mode]);

  const isCalendarLoading = !isLoaded || isLoading;
  const events = isCalendarLoading ? [] : fetchedEvents;

  /** Persist the RBC view locally and in calendar preferences. */
  const handleViewChange = React.useCallback(
    (nextView: View) => {
      setView(nextView);
      saveCalendarConfig(toCalendarPreferenceRecord(applyCalendarViewPreference(cal, nextView)));
    },
    [cal, saveCalendarConfig],
  );

  /** Move the visible period; the fetch window follows. */
  const handleNavigate = React.useCallback((nextDate: Date) => {
    setDate(nextDate);
  }, []);

  function handleVacationZoneFilterChange(value: string): void {
    saveCalendarConfig(toCalendarPreferenceRecord(applyVacationZonePreference(cal, value)));
  }

  function toggleType(type: CalendarEventType): void {
    setEnabledTypes((prev) => toggleCalendarEventType(prev, type));
  }

  const filteredEvents = React.useMemo(
    () => filterCalendarEventsByType(events, requestedTypes),
    [events, requestedTypes],
  );

  const calendarEvents = React.useMemo(
    () => buildRbcEventsForView(filteredEvents, requestedTypes, view),
    [filteredEvents, requestedTypes, view],
  );

  const openOverflowDay = React.useCallback((cellDate: Date, cellEvents: CalendarEvent[]) => {
    setDayEventsModalDate(cellDate);
    setDayEventsModalEvents(cellEvents);
    setSelectedEventInModal(null);
  }, []);

  const openLaneEventDetail = React.useCallback((laneEvent: CalendarEvent) => {
    setSelectedEventInModal(laneEvent);
    setDayEventsModalDate(null);
    setDayEventsModalEvents([]);
  }, []);

  const renderAgendaEvent = React.useCallback(
    function renderAgendaEvent(props: { event: CalendarRbcEvent; title: string }) {
      return (
        <AgendaEvent
          event={props.event}
          title={props.title}
          onSelectLaneEvent={openLaneEventDetail}
          onSelectLaneOverflow={openOverflowDay}
        />
      );
    },
    [openLaneEventDetail, openOverflowDay],
  );

  const calendarComponents = React.useMemo(
    () => ({
      toolbar: CalendarToolbar,
      agenda: { event: renderAgendaEvent },
    }),
    [renderAgendaEvent],
  );

  /** Month / day / week header `+N`; agenda keeps column title alignment. */
  React.useEffect(() => {
    const root = document.querySelector('.calendar-container');
    if (!root) return;

    let detach = () => {};
    const timer = window.setTimeout(() => {
      root.querySelectorAll('.rbc-date-cell-badge').forEach((badge) => {
        const anchor = badge.parentElement;
        if (anchor?.classList.contains('calendar-day-column-badge-anchor')) {
          anchor.remove();
        } else {
          badge.remove();
        }
      });

      if (view === 'agenda') {
        alignAgendaColumnTitles(root);
        return;
      }

      const badges: HTMLElement[] = [];
      const listeners = new Map<HTMLElement, (event: Event) => void>();

      const attachHeaderOverflowBadge = (
        host: Element,
        cellDate: Date,
        maxVisible: number,
        useAnchor = false,
      ) => {
        const hiddenEvents = getTimeGridHiddenEventsOnDay(filteredEvents, cellDate, maxVisible);
        if (hiddenEvents.length <= 0) return;

        const clickHandler = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();
          openOverflowDay(cellDate, hiddenEvents);
        };
        const badge = createDayEventsBadge(hiddenEvents.length, undefined, clickHandler, 'neutral');
        listeners.set(badge, clickHandler);
        badges.push(badge);

        if (useAnchor) {
          const anchor = document.createElement('div');
          anchor.className = 'calendar-day-column-badge-anchor';
          anchor.appendChild(badge);
          host.appendChild(anchor);
          return;
        }
        host.appendChild(badge);
      };

      if (view === 'month') {
        const processedDates = new Set<string>();
        root.querySelectorAll('.rbc-month-view .rbc-date-cell').forEach((cell) => {
          const dateText = cell.textContent?.trim();
          if (!dateText) return;
          const dayNumber = Number.parseInt(dateText, 10);
          if (Number.isNaN(dayNumber)) return;

          const cellDate = resolveCellCalendarDate(
            date,
            dayNumber,
            cell.classList.contains('rbc-off-range'),
          );
          const dateKey = `${cellDate.getFullYear()}-${cellDate.getMonth()}-${cellDate.getDate()}`;
          if (processedDates.has(dateKey)) return;
          processedDates.add(dateKey);

          const cellEvents = getEventsOnDay(filteredEvents, cellDate);
          const now = new Date();
          const primaryEvent = getFullCalendarPrimaryEvent(cellEvents, requestedTypes, now);
          const overflowEvents = getMonthOverflowEvents(cellEvents, requestedTypes, now);
          if (overflowEvents.length <= 0) return;

          const clickHandler = (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
            openOverflowDay(cellDate, overflowEvents);
          };
          const badge = createDayEventsBadge(
            overflowEvents.length,
            primaryEvent?.type,
            clickHandler,
          );
          listeners.set(badge, clickHandler);
          badges.push(badge);
          cell.appendChild(badge);
        });
      } else if (view === 'week' || view === 'work_week') {
        const headerRow = root.querySelector('.rbc-time-header-cell');
        if (headerRow) {
          const headers = headerRow.querySelectorAll(':scope > .rbc-header');
          const headerDates = timeGridHeaderDates(date, headers.length);
          headers.forEach((header, index) => {
            const cellDate = headerDates[index];
            if (!cellDate) return;
            attachHeaderOverflowBadge(header, cellDate, CALENDAR_CLUSTER_MAX_VISIBLE.week);
          });
        }
      } else if (view === 'day') {
        const cellDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayHeader =
          root.querySelector('.rbc-time-header-cell-single-day > .rbc-header') ??
          root.querySelector('.rbc-time-header-cell > .rbc-header');
        if (dayHeader) {
          attachHeaderOverflowBadge(dayHeader, cellDate, CALENDAR_CLUSTER_MAX_VISIBLE.day);
        }
      }

      detach = () => {
        listeners.forEach((handler, badge) => badge.removeEventListener('click', handler));
        badges.forEach((badge) => {
          const anchor = badge.parentElement;
          if (anchor?.classList.contains('calendar-day-column-badge-anchor')) {
            anchor.remove();
          } else {
            badge.remove();
          }
        });
      };
    }, 100);

    return () => {
      window.clearTimeout(timer);
      detach();
    };
  }, [view, filteredEvents, date, requestedTypes, openOverflowDay]);

  function renderLegendItem(item: CalendarLegendItem): React.ReactNode {
    return (
      <div key={item.itemClass} className={`calendar-legend-item ${item.itemClass}`}>
        <span className="calendar-legend-dot" />
        <span>{item.label}</span>
      </div>
    );
  }

  function handleSelectRbcEvent(event: CalendarRbcEvent): void {
    if (isCalendarAgendaLaneRbcEvent(event) && event.laneEvents) {
      if (event.laneEvents.length === 1) {
        openLaneEventDetail(event.laneEvents[0]);
        return;
      }
      openOverflowDay(event.start, event.laneEvents);
      return;
    }
    setSelectedEventInModal(event.resource);
    setDayEventsModalDate(null);
    setDayEventsModalEvents([]);
  }

  function handleCloseDayModal(): void {
    setDayEventsModalDate(null);
    setDayEventsModalEvents([]);
    setSelectedEventInModal(null);
  }

  function handleEditFromModal(sourceId: string, type: string): void {
    let allEvents: CalendarEvent[] = [];
    if (dayEventsModalDate) {
      allEvents = dayEventsModalEvents;
    } else if (selectedEventInModal) {
      allEvents = [selectedEventInModal];
    }
    const eventToEdit = allEvents.find((item) => item.sourceId === sourceId && item.type === type);
    if (eventToEdit) {
      onSelectEvent?.(eventToEdit);
    }
  }

  return (
    <div className="calendar-page space-y-5">
      <CalendarFilters
        zoneValue={vacationZoneFilter}
        onZoneChange={handleVacationZoneFilterChange}
        activeTypes={requestedTypes}
        onToggleType={toggleType}
        layoutWidth={layoutWidth}
        showFieldEvents={showFieldEvents}
      />

      <div className="calendar-container">
        <LoadingOverlay isLoading={isCalendarLoading} spinnerSize="lg" message="Chargement..." />
        <Calendar<CalendarRbcEvent>
          localizer={calendarLocalizer}
          events={calendarEvents}
          startAccessor={'start' as any}
          endAccessor={'end' as any}
          style={{ height: '100%' }}
          min={CALENDAR_DAY_VIEW_MIN}
          max={CALENDAR_DAY_VIEW_MAX}
          scrollToTime={CALENDAR_DAY_VIEW_SCROLL_TO}
          enableAutoScroll
          onSelectEvent={handleSelectRbcEvent}
          view={view}
          onView={handleViewChange}
          date={date}
          onNavigate={handleNavigate}
          eventPropGetter={getCalendarEventBlockStyle}
          dayLayoutAlgorithm="no-overlap"
          culture="fr"
          messages={CALENDAR_RBC_MESSAGES}
          components={calendarComponents}
        />
      </div>

      <CalendarEventModal
        isOpen={!!dayEventsModalDate || !!selectedEventInModal}
        onClose={handleCloseDayModal}
        event={selectedEventInModal}
        dayEvents={dayEventsModalDate ? dayEventsModalEvents : undefined}
        onEdit={handleEditFromModal}
      />

      <div className="calendar-legend-block">
        <h3 className="calendar-legend-title">Légende</h3>
        <div className="calendar-legend">{legendItems.map(renderLegendItem)}</div>
      </div>
    </div>
  );
};
